import { Link, useParams } from "react-router-dom";
import { MapPin } from "lucide-react";
import { PageHero, Section, SectionHeading, Seo, Reveal } from "@/components/site/Section";
import { PostcodeChecker } from "@/components/site/PostcodeChecker";
import { PlansGrid } from "@/components/site/PlanCard";
import { FAQList } from "@/components/site/FAQList";
import { FinalCTA } from "@/components/site/HomeSections";
import SEO from "@/data/seo-pages.json";
import NotFound from "@/pages/NotFound";

const SITE = process.env.REACT_APP_CANONICAL_URL;

const areaLd = (a, path) => [
  { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "Delivery Areas", item: `${SITE}/delivery-areas` },
    { "@type": "ListItem", position: 3, name: a.name, item: `${SITE}${path}` }] },
  { "@context": "https://schema.org", "@type": "Service", name: `Firewood delivery in ${a.name}`, serviceType: "Firewood and log delivery subscription",
    provider: { "@id": `${SITE}/#business` }, url: `${SITE}${path}`,
    areaServed: [...a.towns.map((t) => ({ "@type": "Place", name: t })), { "@type": "PostalCodeRangeSpecification", postalCodeBegin: a.postcode, postalCodeEnd: a.postcode }] },
  { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: a.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) },
];

const OtherAreas = ({ current }) => (
  <div className="flex flex-wrap gap-3" data-testid="other-areas">
    {SEO.areas.filter((a) => a.slug !== current).map((a) => (
      <Link key={a.slug} to={`/log-delivery/${a.slug}`} className="rounded-full border border-[#262626] px-4 py-2 text-sm text-[#F7F5F0]/80 transition-colors hover:border-[#FF6A00] hover:text-[#FF6A00]" data-testid={`area-link-${a.slug}`}>
        {a.name} ({a.postcode})
      </Link>
    ))}
  </div>
);

export default function AreaPage() {
  const { slug } = useParams();
  const a = SEO.areas.find((x) => x.slug === slug);
  if (!a) return <NotFound />;
  const path = `/log-delivery/${a.slug}`;
  return (
    <>
      <Seo title={a.title} description={a.description} path={path} jsonLd={areaLd(a, path)} />
      <PageHero eyebrow={`Delivery area · ${a.postcode}`} title={a.h1} intro={a.intro}>
        <div className="mt-10 max-w-2xl" data-testid="area-postcode-check"><PostcodeChecker idPrefix="area-page-postcode" /></div>
      </PageHero>
      <Section>
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <SectionHeading eyebrow="Towns we cover" title={`Logs delivered across ${a.postcode}.`} />
            <ul className="mt-8 grid grid-cols-2 gap-3" data-testid="area-towns">
              {a.towns.map((t) => <li key={t} className="flex items-center gap-2 text-[#F7F5F0]/85"><MapPin className="h-4 w-4 text-[#FF6A00]" />{t}</li>)}
            </ul>
          </div>
          <Reveal delay={0.1} className="space-y-5 lg:col-span-7">
            {a.body.map((p) => <p key={p.slice(0, 20)} className="text-base leading-relaxed text-[#A1A1AA] md:text-lg">{p}</p>)}
          </Reveal>
        </div>
      </Section>
      <Section className="!pt-0">
        <SectionHeading eyebrow="Plans" title={`Firewood plans for ${a.name}.`} />
        <div className="mt-10"><PlansGrid /></div>
      </Section>
      <Section className="!pt-0">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5"><SectionHeading eyebrow="Questions" title={`Log delivery in ${a.name}: your questions.`} /></div>
          <div className="lg:col-span-7"><FAQList items={a.faqs} prefix={`area-faq-${a.slug}`} /></div>
        </div>
        <div className="mt-14">
          <p className="mb-4 text-sm uppercase tracking-[0.2em] text-[#D4A437]">Other areas we deliver to</p>
          <OtherAreas current={a.slug} />
        </div>
      </Section>
      <FinalCTA />
    </>
  );
}
